import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Router, ActivatedRoute, UrlSegment, Params } from '@angular/router';
import { isPlatformBrowser, isPlatformServer, Location } from '@angular/common';
import { ServerResponseService } from '@app/shared/services/server-response/server-response.service';
import { UrlGeneratorService } from '@app/core/i18n';
import { environment } from '@env/environment';
import { Logger } from '@app/core/logging';

export interface UrlOptions {
  params?: boolean;
  fragment?: boolean;
}

@Injectable()
export class RoutingService {
  private environment: string = environment.envName;

  constructor(
    private logger: Logger,
    private router: Router,
    private serverResponse: ServerResponseService,
    private urlGenerator: UrlGeneratorService,
    private serverResponseService: ServerResponseService,
    @Inject('HOST') private hostname: string,
    @Inject('PROTOCOL') private protocol: string,
    @Inject(PLATFORM_ID) private platformId: Object,
    private location: Location
  ) {}

  getHostname(): string {
    return this.hostname;
  }

  getProtocol(): string {
    return this.protocol;
  }

  buildPath(segments: UrlSegment[]): string {
    let path = segments.join('/');

    if (!path.startsWith('/')) {
      path = '/' + path;
    }

    if (!path.endsWith('/') && !path.endsWith('.shtml')) {
      path += '/';
    }

    return path;
  }

  buildUrl(route: ActivatedRoute, options?: UrlOptions): string {
    const url = this.router.createUrlTree(['.'], {
      preserveFragment: options.fragment,
      relativeTo: route
    });

    return this.router.serializeUrl(url);
  }

  sendMisdirectedRequest(): void {
    if (isPlatformServer(this.platformId)) {
      this.serverResponse.setStatus(421, 'Misdirected Request');
    }
  }

  failover(url: string): void {
    this.logger.notice(`Misdirected Request ${url}`);

    let hostname = '';

    if (this.environment === 'dev') {
      hostname = '//' + environment.legacyLink;
    } else if (this.environment === 'stage') {
      hostname = this.urlGenerator
        .generateUrl('/')
        .replace(/\/$/, '')
        .replace(/\/\/us\./, '//www.')
        .replace('staging', 'stage');
    }

    const redirectUrl = hostname + url;

    if (isPlatformBrowser(this.platformId)) {
      this.logger.debug(`Redirect to failover ${redirectUrl}`);
      location.href = redirectUrl;
    }
    this.sendMisdirectedRequest();
  }

  getCatalogPaginationMatch(url: string) {
    return url.match(/^(.*\/)p-(\d*)\//i);
  }

  getCampaignId(url: string): string {
    const [, , id] = url.match(/(.+)-([0-9]+)/);
    return id;
  }

  redirectTo(destination: string, queryParams?: Params) {
    if (queryParams) {
      const url = this.router.createUrlTree([destination, ''], {
        queryParams
      });
      destination = this.router.serializeUrl(url);
    }

    if (isPlatformServer(this.platformId)) {
      this.serverResponseService.redirect(301, destination);
    } else {
      this.router.navigateByUrl(destination);
    }
  }

  redirectError(statusCode, url) {
    this.router
      .navigate(['/not-found'], { skipLocationChange: true, queryParams: { statusCode, url } })
      .then(() => this.location.go(url));
  }
}
